export type ContactTableColumnId =
  | "firstName"
  | "lastName"
  | "displayName"
  | "email"
  | "phone"
  | "birthday"
  | "groups"
  | "createdAt"
  | "updatedAt"
  | "platforms";

export type ContactTableUiDir = "ltr" | "rtl";

export const CONTACT_TABLE_COLUMNS: Array<{ id: ContactTableColumnId }> = [
  { id: "firstName" },
  { id: "lastName" },
  { id: "displayName" },
  { id: "email" },
  { id: "phone" },
  { id: "birthday" },
  { id: "groups" },
  { id: "platforms" },
  { id: "createdAt" },
  { id: "updatedAt" },
];

/** Default display order for all table columns. */
export const DEFAULT_COLUMN_ORDER: ContactTableColumnId[] = [
  "firstName",
  "lastName",
  "displayName",
  "email",
  "phone",
  "birthday",
  "groups",
  "createdAt",
  "updatedAt",
  "platforms",
];

/** Optional columns hidden on first visit; user can show any column. Channels is visible by default. */
export const DEFAULT_HIDDEN_COLUMNS: ContactTableColumnId[] = ["displayName", "birthday", "updatedAt"];

/** No columns are pinned on first visit. */
export const DEFAULT_PINNED_COLUMNS: ContactTableColumnId[] = [];

/** Fixed chrome widths for sticky offset math (px). */
export const CONTACT_TABLE_CHECKBOX_WIDTH = 40;
export const CONTACT_TABLE_ACTIONS_WIDTH = 44;

/**
 * Default data-column widths used for multi-pin sticky offsets.
 * Not user-resizable in Phase A1; kept inside the existing column definition system.
 */
export const CONTACT_TABLE_DEFAULT_WIDTHS: Record<ContactTableColumnId, number> = {
  firstName: 140,
  lastName: 140,
  displayName: 160,
  email: 200,
  phone: 168,
  birthday: 144,
  groups: 180,
  platforms: 128,
  createdAt: 148,
  updatedAt: 148,
};

/** @deprecated Use DEFAULT_COLUMN_ORDER + visibleColumns from useContactTableColumns */
export const DEFAULT_VISIBLE_COLUMNS: ContactTableColumnId[] = DEFAULT_COLUMN_ORDER.filter(
  (id) => !DEFAULT_HIDDEN_COLUMNS.includes(id),
);

export function allContactTableColumnIds(): ContactTableColumnId[] {
  return CONTACT_TABLE_COLUMNS.map((col) => col.id);
}

export function isContactTableColumnId(value: string): value is ContactTableColumnId {
  return allContactTableColumnIds().includes(value as ContactTableColumnId);
}

export function normalizeContactColumnOrder(
  order: readonly string[],
): ContactTableColumnId[] {
  const next: ContactTableColumnId[] = [];
  for (const id of order) {
    if (isContactTableColumnId(id) && !next.includes(id)) {
      next.push(id);
    }
  }
  for (const id of allContactTableColumnIds()) {
    if (!next.includes(id)) {
      next.push(id);
    }
  }
  return next;
}

export function normalizePinnedColumns(
  pinned: readonly string[],
  order: readonly ContactTableColumnId[],
): ContactTableColumnId[] {
  const known = new Set(order);
  const next: ContactTableColumnId[] = [];
  for (const id of pinned) {
    if (isContactTableColumnId(id) && known.has(id) && !next.includes(id)) {
      next.push(id);
    }
  }
  return next;
}

/** Pinned columns that are currently visible, in pin-sequence order. */
export function pinnedVisibleColumns(
  pinned: readonly ContactTableColumnId[],
  hidden: ReadonlySet<ContactTableColumnId>,
): ContactTableColumnId[] {
  return pinned.filter((id) => !hidden.has(id));
}

/**
 * Display order for the table:
 * 1. pinned visible columns in pin sequence
 * 2. remaining visible columns in saved base `order`
 */
export function contactTableVisibleColumns(
  baseOrder: readonly ContactTableColumnId[],
  hidden: ReadonlySet<ContactTableColumnId>,
  pinned: readonly ContactTableColumnId[],
): ContactTableColumnId[] {
  const pinnedSet = new Set(pinned);
  const pinnedVisible = pinnedVisibleColumns(pinned, hidden);
  const unpinnedVisible = baseOrder.filter((id) => !hidden.has(id) && !pinnedSet.has(id));
  return [...pinnedVisible, ...unpinnedVisible];
}

export function moveContactTableColumn(
  list: readonly ContactTableColumnId[],
  fromId: ContactTableColumnId,
  toId: ContactTableColumnId,
): ContactTableColumnId[] {
  if (fromId === toId) return [...list];
  const from = list.indexOf(fromId);
  const to = list.indexOf(toId);
  if (from < 0 || to < 0) return [...list];
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function appendPinnedColumn(
  pinned: readonly ContactTableColumnId[],
  id: ContactTableColumnId,
): ContactTableColumnId[] {
  if (pinned.includes(id)) return [...pinned];
  return [...pinned, id];
}

export function removePinnedColumn(
  pinned: readonly ContactTableColumnId[],
  id: ContactTableColumnId,
): ContactTableColumnId[] {
  return pinned.filter((columnId) => columnId !== id);
}

/**
 * Cumulative sticky offset from logical START for a pinned visible column.
 * Includes the always-sticky checkbox chrome width.
 */
export function contactPinnedStickyOffset(
  columnId: ContactTableColumnId,
  pinnedVisible: readonly ContactTableColumnId[],
  widths: Record<ContactTableColumnId, number> = CONTACT_TABLE_DEFAULT_WIDTHS,
): number {
  let offset = CONTACT_TABLE_CHECKBOX_WIDTH;
  for (const id of pinnedVisible) {
    if (id === columnId) {
      return offset;
    }
    offset += widths[id];
  }
  return offset;
}

/**
 * Sticky pin to logical START.
 * Uses physical left/right because `inset-inline-start` is unreliable on sticky table cells.
 * LTR → left; RTL → right.
 */
export function contactStickyStartStyle(
  offsetPx: number,
  dir: ContactTableUiDir,
): { left: number | "auto"; right: number | "auto" } {
  return dir === "rtl"
    ? { right: offsetPx, left: "auto" }
    : { left: offsetPx, right: "auto" };
}

/**
 * Sticky pin to logical END (actions column).
 * LTR → right; RTL → left.
 */
export function contactStickyEndStyle(
  offsetPx: number,
  dir: ContactTableUiDir,
): { left: number | "auto"; right: number | "auto" } {
  return dir === "rtl"
    ? { left: offsetPx, right: "auto" }
    : { right: offsetPx, left: "auto" };
}
